import ProtectedRoute from 'authorization-module/lib/ui/atoms/ProtectedRoute'
import config from 'const/config'
import featureFlags from 'const/featureFlags'
import routes from 'const/routes'
import AccountsDashboard from 'pages/AccountsDashboard'
import { FullPageRewardsError } from 'pages/AccountsDashboard/TechnicalError/subcomponents'
import AccountServices from 'pages/AccountServices'
import Activity from 'pages/Activity'
import Alerts from 'pages/Alerts'
import Callback from 'pages/Callback'
import CreditBalanceRefund from 'pages/CreditBalanceRefund'
import CreditView from 'pages/CreditView'
import CreditViewError from 'pages/CreditView/CreditViewError'
import DisputesCenter from 'pages/DisputesCenter'
import ErrorPage from 'pages/ErrorPage'
import Logout from 'pages/Logout'
import PageLayout from 'pages/PageLayout'
import PageNotFound from 'pages/PageNotFound'
import PdfStatementPage from 'pages/PdfStatement'
import MarketingAndPrivacy from 'pages/PreferenceSettings'
import Profile from 'pages/Profile'
import PromotionsPage from 'pages/Promotions'
import Rewards from 'pages/Rewards'
import VantageScore from 'pages/VantageScore'
import { Route, Routes, useLocation } from 'react-router-dom'
import isMockData from 'syf-js-utilities/helpers/isMockData'
import DetailsModal from './DetailsModal'
import FullPageRoute from './FullPageRoute'
import { OutletWrapperHOC, RouteWrapper } from './subcomponents'

/**
 * Renders the Accounts Dashboard or a fallback component based on feature flag and route conditions.
 * The feature flag is short term solution since this is first time MFE modal is using routes.
 * After a successful launch and testing, we will remove this flag and the associated logic.
 */
const RenderDashboardWithOutlet = ({ isAccountDetailsRouteEnabled }) => {
  const location = useLocation()

  if (
    !isAccountDetailsRouteEnabled &&
    location.pathname === routes.ACCOUNT_DETAILS
  ) {
    return <PageNotFound />
  }

  if (isAccountDetailsRouteEnabled) {
    // if feature flag is enabled, allow child routes
    return (
      <OutletWrapperHOC>
        <AccountsDashboard />
      </OutletWrapperHOC>
    )
  }

  // if feature flag is not enabled, allow only root
  return <AccountsDashboard />
}

const AppRoutes = ({
  authenticateCallback,
  authorizedCallback,
  setTokenFetched
}) => {
  const isUsingMockData = isMockData(config.APP_ENV)

  return (
    <RouteWrapper>
      <Routes>
        <Route
          path=""
          element={
            <ProtectedRoute
              authenticateCallback={authenticateCallback}
              authorizedCallback={authorizedCallback}
              isMockData={isUsingMockData}
            />
          }
        >
          <Route
            path=""
            element={
              <RenderDashboardWithOutlet
                isAccountDetailsRouteEnabled={
                  featureFlags.isAccountDetailsRouteEnabled
                }
              />
            }
          >
            {featureFlags.isAccountDetailsRouteEnabled && (
              <Route path={routes.ACCOUNT_DETAILS} element={<DetailsModal />} />
            )}
          </Route>
        </Route>
        <Route
          path={routes.CALLBACK}
          element={<Callback setTokenFetched={setTokenFetched} />}
        />
        <Route path={routes.ERROR} element={<ErrorPage />} />
        <Route element={<PageLayout />}>
          <Route path={routes.ACTIVITY} element={<Activity />} />
          <Route path={routes.DISPUTES} element={<DisputesCenter />} />
          <Route
            path={routes.PROMOTIONS}
            element={
              <FullPageRoute featureFlag={featureFlags.isPromotionsMfeEnabled}>
                <PromotionsPage />
              </FullPageRoute>
            }
          />
          <Route path={routes.ACCOUNT_SERVICES} element={<AccountServices />} />
          <Route
            path={routes.REWARDS}
            element={
              <FullPageRoute
                featureFlag={featureFlags.isRewardsDeeplinkEnabled}
              >
                <Rewards />
              </FullPageRoute>
            }
          />
        </Route>
        <Route
          path={routes.STATEMENT}
          element={
            <FullPageRoute featureFlag={featureFlags.isStatementsMfeEnabled}>
              <PdfStatementPage />
            </FullPageRoute>
          }
        />
        <Route path={routes.LOGOUT} element={<Logout />} />
        <Route path={routes.REWARDS_ERROR} element={<FullPageRewardsError />} />
        <Route
          path={routes.CREDIT_BALANCE_REFUND}
          element={<CreditBalanceRefund />}
        />
        <Route
          path={routes.CREDIT_VIEW}
          element={
            <FullPageRoute
              featureFlag={featureFlags.isCreditViewDashboardEnabled}
            >
              <CreditView />
            </FullPageRoute>
          }
        />
        <Route path={routes.CREDIT_VIEW_ERROR} element={<CreditViewError />} />
        <Route path={routes.PROFILE} element={<Profile />} />
        <Route path={routes.ALERTS} element={<Alerts />} />
        {featureFlags.isMarketingPrivacySettingEnabled && (
          <Route
            path={routes.MARKETING_PRIVACY}
            element={<MarketingAndPrivacy />}
          />
        )}
        {featureFlags.isVantageScoreMfeEnabled && (
          <Route path={routes.CREDIT_SCORE} element={<VantageScore />} />
        )}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </RouteWrapper>
  )
}

export default AppRoutes
